import { ChangeEvent, Fragment, ReactElement, useEffect, useRef, useState, MouseEvent, ReactNode } from 'react';
import { IPage } from '../Components/Page';
import { EventCheckMode, eventStore, IStoreEvent, IStoreGate } from '../Services/EventsStore';
import { execAsync, formatValidationMessage, useInitAsync } from '../Utils';
import Home from './Home';
import Button from '@mui/material/Button';
import session from '../Services/Session';
import SearchText, { ISearchTextManager } from '../Components/SearchText';
import QrCodeIcon from '@mui/icons-material/QrCode';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import GppGoodIcon from '@mui/icons-material/GppGood';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Checkbox from '@mui/material/Checkbox';
import Fab from '@mui/material/Fab';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReportIcon from '@mui/icons-material/Report';
import RefreshIcon from '@mui/icons-material/Refresh';
import { ReactComponent as TicketSearchIcon } from '../Images/search-tik.svg';
import { useOperation } from '../Hooks/OperationContext';
import Field from '../Components/Field';
import { appSettings } from '../AppSettings';
import { useNetwork, useOnlineCheck } from '../Hooks/Network';
import "./TicketSearch.scss"
import { useParams } from 'react-router';
import Title from '../Components/Title';
import { ticketValidator } from '../Services/TicketValidator';
import { ITicketData } from '../Services/ApiClient';

interface ITicketStatus {
    type: "selected" | "validating" | "valid" | "invalid";
    message?: string;
}

interface IFilterItem {
    text: string;
    icon: ReactElement,
    value: string
}

const filters: IFilterItem[] = [{
    text: "Nome e Cognome",
    icon: <ConfirmationNumberIcon />,
    value: "ticket_holder"
},
{
    text: "Numero ordine",
    icon: <GppGoodIcon />,
    value: "order_number"
},
{
    text: "Codice a barre",
    icon: <QrCodeIcon />,
    value: "barcode"
}
]

interface ITicketSearchProps {
    event?: IStoreEvent;
    gate?: IStoreGate;
}


export function TicketSearchContent(props: ITicketSearchProps) {

    let searchManager: ISearchTextManager;

    const operation = useOperation();

    const network = useNetwork();

    const [filterType, setFilterType] = useState("ticket_holder");

    const [filterAnchor, setFilterAnchor] = useState(null);

    const [tickets, setTickets] = useState(null as ITicketData[]);

    const [searchText, setSearchText] = useState("");

    const [ticketsStatus, setTicketsStatus] = useState({} as { [code: string]: ITicketStatus });

    const [event, setEvent] = useState(props.event);

    const [gate, setGate] = useState(props.gate);

    const checkInRef = useRef<HTMLButtonElement>();

    let { eventId, gateId } = useParams();

    const MIN_SEARCH_CHARS = appSettings.minSearchChars ?? 3;

    const onlineCheck = useOnlineCheck();

    useInitAsync(() => network(async () => {

        if (!event && eventId) {
            session.activeEvent = await eventStore.getEventByIdAsync(eventId);
            setEvent(session.activeEvent);
        }

        if (!gate && gateId) {
            session.activeGate = await eventStore.getEventGateAsync(eventId, gateId);
            setGate(session.activeGate);
        }

        await onlineCheck();

    }));

    useEffect(() => {

        execAsync(async () => {
            if (!searchText || !filterType || searchText.length < MIN_SEARCH_CHARS) {
                setTickets(null);
                setTicketsStatus({});
            }
            else {

                setTickets(null);
                setTicketsStatus({});

                const result = await network(() => ticketValidator.searchTicketsAsync({
                    eventID: eventId,
                    searchMethod: filterType,
                    searchString: searchText
                }));

                if (appSettings.searchLimit && result.length > appSettings.searchLimit)
                    result.splice(appSettings.searchLimit, result.length - appSettings.searchLimit);

                setTickets(result);

                if (tickets?.length == 1 && !tickets[0].dataingressocontrollo)
                    selectAll();

            }
        });
    }, [searchText, filterType]);


    const search = (text: string) => {
        setSearchText(text);
        setTicketsStatus({});
    }

    const onSelectFilter = (ev) => {
        setFilterAnchor(ev.currentTarget);
    }

    const onFilterSelected = (filter?: IFilterItem) => {
        setFilterAnchor(null);
        if (filter) {
            if (searchManager) {
                searchManager.clear();
                searchManager.focus();
            }
            setFilterType(filter.value);
        }
    }

    const onItemSelected = (ev: ChangeEvent, item: ITicketData, value: boolean) => {

        if (value)
            ticketsStatus[item.barcode] = { type: "selected" }
        else
            delete ticketsStatus[item.barcode];

        setTicketsStatus({ ...ticketsStatus });
    }

    let selCount = 0;
    for (let key in ticketsStatus)
        if (ticketsStatus[key].type === "selected")
            selCount++; 

    const onCheckIn = async () => {

        operation.begin({ showProgress: false });

        for (const ticket of tickets) {

            const status = ticketsStatus[ticket.barcode];

            if (status?.type !== "selected")
                continue;

            const result = await network(async () => {

                ticketsStatus[ticket.barcode] = { type: "validating" };
                setTicketsStatus({ ...ticketsStatus });

                const verResult = (await ticketValidator.verifyAsync({
                    eventID: eventId,
                    barcode: ticket.barcode,
                    ref_gate: gateId,
                    direction: 'E',
                    ref_device: session.deviceId
                }))[0];

                if (verResult.type == "OK")
                    ticketsStatus[ticket.barcode] = { type: "valid" }
                else
                    ticketsStatus[ticket.barcode] = { type: "invalid", message: verResult.msg }

                setTicketsStatus({ ...ticketsStatus });

                if (selCount == 1)
                    operation.showAlert(formatValidationMessage(verResult.msg, "long"), verResult.type == "OK" ? "success" : "error", 4000);

                return true;
            }, { isLocal: true });

            if (!result) {
                ticketsStatus[ticket.barcode] = { type: "invalid", message: "Errore di connessione" }
                setTicketsStatus({ ...ticketsStatus });
                break;
            }
        }

        operation.end();

    };

    const selectAll = () => {
        const newStatus = { ...ticketsStatus };
        for (const ticket of tickets)
            newStatus[ticket.barcode] = { type: "selected" }
        setTicketsStatus(newStatus);
    }

    const curFilter = filters.find(a => a.value == filterType);

    let searchInfo: ReactNode;

    if (searchText) {
        if (searchText.length < MIN_SEARCH_CHARS)
            searchInfo = "Inserire almeno " + MIN_SEARCH_CHARS + " caratteri";
        else {
            if (tickets == null)
                searchInfo = (<Fragment>Ricerca <span className="keyword">{searchText}</span> in corso</Fragment>);
            else if (tickets.length == 0)
                searchInfo = (<Fragment>Nessun risultato per <span className="keyword">{searchText}</span></Fragment>);
            else
                searchInfo = (<Fragment><span className="count">{tickets.length}</span> {tickets.length == 1 ? "risultato" : "risultati"} per <span className="keyword">{searchText}</span></Fragment>);
        }
    }
    else
        searchInfo = null;

    return (
        <Fragment>
            <header>
                <div className="sub-title">Ricerca Ticket</div>
                <Title>{event?.title}</Title>
            </header>
            <div className="search-box">
                <SearchText manager={a => searchManager = a} onChanged={search} placeholder={curFilter.text} />
                <Button onClick={onSelectFilter}>{curFilter.icon}</Button>
            </div>
            {searchInfo ?
                <div className="search-info">{searchInfo}</div>
                : null
            }
            <Menu
                id="basic-menu"
                anchorEl={filterAnchor}
                open={filterAnchor != null}
                onClose={() => onFilterSelected()}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
            >
                {filters.map(a => (
                    <MenuItem key={a.value} onClick={() => onFilterSelected(a)}>
                        <ListItemIcon>{a.icon}</ListItemIcon>
                        <ListItemText>{a.text}</ListItemText>
                    </MenuItem>))
                }
            </Menu>
            <div className="filters">
                {
                    filters.filter(a => a.value != filterType).map(a =>
                        <Button key={a.value} variant="contained" onClick={() => onFilterSelected(a)}>{a.icon}<span>{a.text}</span></Button>)
                }
            </div>
            {(tickets && tickets.length == 0) ?
                <div className="empty-list">
                    <ConfirmationNumberIcon />
                    <span>Nessun biglietto trovato</span>
                </div>
                :
                <div className="list-view">
                    {tickets?.map((ticket) => {

                        const dataemissione = !isNaN(ticket.dataemissione as number) ? ticket.dataemissione?.toString() : ticket.dataemissione as string;
                        const oraemissione = !isNaN(ticket.oraemissione as any) ? ticket.oraemissione?.toString() : ticket.oraemissione as string;

                        const emissionDate = dataemissione && oraemissione ? 
                            (dataemissione.substr(6, 2) + "/" +
                            dataemissione.substr(4, 2) + "/" +
                            dataemissione.substr(0, 4) + " " +
                            oraemissione.substr(0, 2) + ":" +
                            oraemissione.substr(2, 2))
                            : null;

                        const dataingressocontrollo = !isNaN(ticket.dataingressocontrollo as number) ? ticket.dataingressocontrollo?.toString() : ticket.dataingressocontrollo as string;
                        const oraingressocontrollo = !isNaN(ticket.oraingressocontrollo as number) ? ticket.oraingressocontrollo?.toString() : ticket.oraingressocontrollo as string;

                        const checkDate = oraingressocontrollo && oraingressocontrollo ?
                            (dataingressocontrollo.substr(6, 2) + "/" +
                            dataingressocontrollo.substr(4, 2) + "/" +
                            dataingressocontrollo.substr(0, 4) + " " +
                            oraingressocontrollo.substr(0, 2) + ":" +
                            oraingressocontrollo.substr(2, 2))
                            : null; 
                      

                        const itemStatus = ticketsStatus[ticket.barcode];

                        let entryStatus = {
                            color: "green",
                            text: "Non Entrato"
                        };

                        if (ticket.stato && ticket.stato.startsWith('A')) {
                            entryStatus = {
                                color: "gray",
                                text: "Annullato"
                            };
                        }

                        if (ticket.dataingressocontrollo || itemStatus?.type == "valid") {
                            entryStatus = {
                                color: "red",
                                text: "Già Entrato"
                            };
                        }
         

                        let checkItem: ReactElement;

                        const onErrorClick = (ev: MouseEvent) => {
                            operation.showAlert(formatValidationMessage(itemStatus.message, "long"), "error", 4000);
                            ev.stopPropagation();
                        }
                    

                        switch (itemStatus?.type) {
                            case "valid":
                                checkItem = <span className="status-icon"><CheckCircleIcon /></span>;
                                break;
                            case "invalid":
                                checkItem = <span className="status-icon" onClick={onErrorClick}><ReportIcon /></span>;
                                break;
                            case "validating":
                                checkItem = <span className="status-icon"><RefreshIcon /></span>;
                                break;
                            default:
                                checkItem = <Checkbox onClick={ev => ev.stopPropagation()} checked={itemStatus?.type === "selected"} onChange={(ev, value) => onItemSelected(ev, ticket, value)} />;
                        }

                        return (
                            <div key={ticket.barcode} className={"list-item " + itemStatus?.type} >
                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />} id={ticket.barcode}>
                                        {checkItem}
                                        <div className="body">
                                            <div className="barcode">{ticket.barcode}</div>
                                            {ticket.nome ?
                                                <div className="name">{ticket.nome + " " + ticket.cognome}</div>
                                                : null}
                                            <span className={"status " + entryStatus.color}>{entryStatus.text}</span>
                                        </div>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <fieldset>
                                            <Field label="Sigillo">{ticket.sigillofiscale}</Field>
                                            <Field label="Ordine">{ticket.riferimentoordine}</Field>
                                            <Field label="Tipo Titolo">{ticket.tipotitolo}</Field>
                                            <Field label="Data Emissione">{emissionDate}</Field>
                                            <Field label="Ora Controllo">{checkDate}</Field>
                                            <Field label="Settore">{ticket.settore}</Field>
                                            <Field label="Fila">{ticket.fila}</Field>
                                            <Field label="Posto">{ticket.posto}</Field>
                                            <Field label="Note Blocco">{ticket.noteblocco}</Field>
                                            <Field label="Carta Attivazione">{ticket.cartaattivazione}</Field>
                                        </fieldset>
                                    </AccordionDetails>
                                </Accordion>
                            </div>
                        )
                    })}
                </div >
            }
            {tickets?.length > 0 ?
                <Button className="select-all" onClick={() => selectAll()}>Seleziona Tutti</Button> :
                null}
            <div className="actions">
                <Fab ref={checkInRef} className={"action-button check-in " + (selCount > 0 ? "visible" : "")} onClick={onCheckIn} variant="extended">
                    <CheckIcon sx={{ mr: 1 }} />
                    {"Marca Ingresso (" + selCount + ")"}
                </Fab>
            </div >
        </Fragment>
    );
}

const TicketSearch: IPage = {

    name: "ticket-search",
    title: "Cerca Ticket",
    route: "/search/:eventId/:gateId",
    icon: <TicketSearchIcon />,
    container: Home,
    content: <TicketSearchContent />,
    isEnabled: () => session.activeEvent != null && session.activeGate != null && session.activeEvent.checkMode == EventCheckMode.Scan,
    appBar: { title: "Access Control", backVisible: true } 
}

export default TicketSearch;